import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { type UserListItem } from "../../../../api/auth";
import { PickupContext } from "../../store/PickupContext";
import { UsersContext } from "../../store/UsersContext";
import { SubscriptionsContext } from "../../store/SubscriptionsContext";
import {
  StepNavPanel,
  type StepNavPanelHandle,
} from "../../modals/StepNavPanel";
import AddCSAToAccountForm, {
  ADD_CSA_TO_ACCOUNT_FORM_ID,
} from "./components/AddCSAToAccountForm";
import "../../modals/Modal.css";
import "./OperationControlsPage.css";

type EditCsaSubscriptionOnClientProps = {
  user: UserListItem;
};

type Flow = "overview" | "add-csa" | "remove-confirm";

type Banner = { type: "success" | "error"; message: string } | null;

const EditCsaSubscriptionOnClient = ({
  user,
}: EditCsaSubscriptionOnClientProps) => {
  const { pickups } = useContext(PickupContext);
  const { users, removeSubscription, sendCsaPaymentReminder } =
    useContext(UsersContext);
  const liveUser = users.find((u) => u.userId === user.userId) ?? user;
  const hasCsaSubscription = liveUser.hasCSASubscription;
  const userId = user.userId;
  const { refetch: refetchSubscriptions } = useContext(SubscriptionsContext);
  const stepNavRef = useRef<StepNavPanelHandle>(null);
  const [flow, setFlow] = useState<Flow>("overview");
  const [banner, setBanner] = useState<Banner>(null);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isAddSubmitting, setIsAddSubmitting] = useState(false);

  const userPickup = pickups.find((pickup) => pickup.userId === userId);
  const paymentStatus = userPickup?.subscriptionPaymentStatus;
  const hasNotPaid = paymentStatus !== "paid";
  const displayName = `${user.firstName} ${user.surname}`;

  const modalTitle =
    flow === "add-csa"
      ? `Add CSA — ${displayName}`
      : flow === "remove-confirm"
        ? `Remove CSA — ${displayName}`
        : `Edit ${displayName}'s CSA subscription`;

  const goToOverview = useCallback(() => {
    setFlow("overview");
    stepNavRef.current?.goToStep(0);
  }, []);

  const startAddCsa = useCallback(() => {
    setBanner(null);
    setFlow("add-csa");
    requestAnimationFrame(() => stepNavRef.current?.goToStep(1));
  }, []);

  const startRemoveConfirm = useCallback(() => {
    setBanner(null);
    setFlow("remove-confirm");
    requestAnimationFrame(() => stepNavRef.current?.goToStep(1));
  }, []);

  const handleSendPaymentReminder = useCallback(async () => {
    if (isSendingReminder) return;
    setIsSendingReminder(true);
    setBanner(null);
    try {
      const result = await sendCsaPaymentReminder(userId);
      setBanner({
        type: "success",
        message: result.emailSent
          ? result.message
          : `${result.message} (email could not be confirmed — check server logs.)`,
      });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to send payment reminder.";
      setBanner({ type: "error", message: msg });
    } finally {
      setIsSendingReminder(false);
    }
  }, [isSendingReminder, sendCsaPaymentReminder, userId]);

  const handleConfirmRemove = useCallback(async () => {
    if (isRemoving) return;
    setIsRemoving(true);
    setBanner(null);
    try {
      await removeSubscription(userId);
      refetchSubscriptions();
      goToOverview();
      setBanner({
        type: "success",
        message: "CSA subscription removed successfully.",
      });
    } catch {
      setBanner({
        type: "error",
        message: "Failed to remove CSA subscription.",
      });
    } finally {
      setIsRemoving(false);
    }
  }, [
    goToOverview,
    isRemoving,
    removeSubscription,
    refetchSubscriptions,
    userId,
  ]);

  const steps = useMemo(() => {
    const overviewStep = {
      content: (
        <div className="edit-csa-subscription-overview">
          {banner && (
            <p
              className={
                banner.type === "success"
                  ? "pickup-form-success-msg"
                  : "pickup-form-validation-msg"
              }
              role="status"
            >
              {banner.message}
            </p>
          )}
          {paymentStatus && (
            <p>
              Payment status:{" "}
              <span className="capitalized">{paymentStatus}</span>
            </p>
          )}
          <div className="buttons-footer">
            {hasCsaSubscription && hasNotPaid && (
              <button
                type="button"
                className="btn"
                disabled={isSendingReminder}
                onClick={() => void handleSendPaymentReminder()}
              >
                {isSendingReminder ? "Sending…" : "Send payment reminder"}
              </button>
            )}
            {hasCsaSubscription ? (
              <button
                type="button"
                className="btn btn-red"
                onClick={startRemoveConfirm}
              >
                Remove CSA
              </button>
            ) : (
              <button type="button" className="btn" onClick={startAddCsa}>
                Add CSA
              </button>
            )}
          </div>
        </div>
      ),
    };

    if (flow === "add-csa") {
      return [
        overviewStep,
        {
          content: (
            <AddCSAToAccountForm
              userId={userId}
              showSubmitButton={false}
              onSubmitChange={setIsAddSubmitting}
              onSuccess={() => {
                refetchSubscriptions();
                goToOverview();
                setBanner({
                  type: "success",
                  message: "This account now has a CSA subscription.",
                });
              }}
              onError={(message) => setBanner({ type: "error", message })}
            />
          ),
          footer: (
            <div className="buttons-footer">
              <button
                type="button"
                className="btn equal-btns"
                onClick={goToOverview}
              >
                Cancel
              </button>
              <button
                type="submit"
                form={ADD_CSA_TO_ACCOUNT_FORM_ID}
                className="btn equal-btns"
                disabled={isAddSubmitting}
              >
                {isAddSubmitting ? "Adding…" : "Add subscription"}
              </button>
            </div>
          ),
        },
      ];
    }

    if (flow === "remove-confirm") {
      return [
        overviewStep,
        {
          content: (
            <div className="operation-controls-modal-step">
              <p className="modal-exit-confirm-msg">
                Are you sure you want to remove the CSA subscription for{" "}
                <span className="bold">{displayName}</span>? This deletes their
                pickup from the list of deliveries. They still keep their social
                account, but can no longer access the dashboard.
              </p>
            </div>
          ),
          footer: (
            <div className="buttons-footer">
              <button
                type="button"
                className="btn equal-btns"
                onClick={goToOverview}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn equal-btns btn-red"
                disabled={isRemoving}
                onClick={() => void handleConfirmRemove()}
              >
                {isRemoving ? "Removing…" : "Yes, remove CSA"}
              </button>
            </div>
          ),
        },
      ];
    }

    return [overviewStep];
  }, [
    banner,
    displayName,
    flow,
    goToOverview,
    handleConfirmRemove,
    handleSendPaymentReminder,
    hasCsaSubscription,
    hasNotPaid,
    isAddSubmitting,
    isRemoving,
    isSendingReminder,
    paymentStatus,
    refetchSubscriptions,
    startAddCsa,
    startRemoveConfirm,
    userId,
  ]);

  return (
    <>
      <h3>{modalTitle}</h3>
      <StepNavPanel ref={stepNavRef} steps={steps} className="modal-step-nav" />
    </>
  );
};

export default EditCsaSubscriptionOnClient;
